import { AnimatePresence, motion } from 'motion/react'
import type { RecoveryFeedbackTone } from './ActivationFlow.types'

type ActivationRecoveryViewProps = {
  success: boolean
  email: string
  emailError: string
  submitting: boolean
  feedback: string
  feedbackTone: RecoveryFeedbackTone
  loginHref: string
  cardEyebrow: string
  cardTitle: string
  cardSubtitle: string
  emailLabel: string
  emailPlaceholder: string
  submitCta: string
  submittingCta: string
  submitHint: string
  securityNote: string
  helper: string
  successTitle: string
  successBody: string
  successHint: string
  successSpamHint: string
  backToLoginLabel: string
  prefersReducedMotion: boolean
  onEmailChange: (value: string) => void
  onEmailBlur: () => void
  onSubmit: () => void
}

export const ActivationRecoveryView = ({
  success,
  email,
  emailError,
  submitting,
  feedback,
  feedbackTone,
  loginHref,
  cardEyebrow,
  cardTitle,
  cardSubtitle,
  emailLabel,
  emailPlaceholder,
  submitCta,
  submittingCta,
  submitHint,
  securityNote,
  helper,
  successTitle,
  successBody,
  successHint,
  successSpamHint,
  backToLoginLabel,
  prefersReducedMotion,
  onEmailChange,
  onEmailBlur,
  onSubmit,
}: ActivationRecoveryViewProps) => {
  if (success) {
    return (
      <div className="space-y-5 text-center">
        <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-status-success)_16%,transparent)] text-(--color-status-success)">
          <svg viewBox="0 0 20 20" className="h-6 w-6 fill-none stroke-current stroke-[2.2]" aria-hidden>
            <path d="m4.5 10.4 3.3 3.3 7.2-7.2" />
          </svg>
        </span>
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">{successTitle}</h2>
          <p className="text-sm leading-relaxed text-(--color-text-secondary)">{successBody}</p>
          <p className="text-xs leading-relaxed text-(--color-text-muted)">{successHint}</p>
          <p className="text-xs leading-relaxed text-(--color-text-muted)">{successSpamHint}</p>
        </div>
        <a
          href={loginHref}
          className="ca-btn-primary inline-flex h-11 w-full items-center justify-center px-4 text-sm font-semibold shadow-[0_10px_24px_-18px_color-mix(in_srgb,var(--color-action-primary)_85%,transparent)] transition-colors duration-200"
        >
          {backToLoginLabel}
        </a>
      </div>
    )
  }

  return (
    <>
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault()
          void onSubmit()
        }}
      >
        <div className="space-y-2 border-b border-[color-mix(in_srgb,var(--color-border-default)_72%,transparent)] pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-(--color-status-info)">{cardEyebrow}</p>
          <h2 className="text-2xl font-semibold tracking-tight text-(--color-text-primary)">{cardTitle}</h2>
          <p className="text-sm leading-relaxed text-(--color-text-secondary)">{cardSubtitle}</p>
        </div>

        <div className="space-y-4 pt-1">
          <label className="grid gap-1.5 text-sm font-medium text-(--color-text-secondary)">
            <span>{emailLabel}</span>
            <input
              type="email"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              onBlur={onEmailBlur}
              autoComplete="email"
              placeholder={emailPlaceholder}
              className={`ca-input h-12 w-full rounded-xl px-3.5 text-sm ${
                emailError ? 'border-(--color-status-error)' : 'border-(--color-border-default)'
              }`}
            />
          </label>

          <AnimatePresence initial={false}>
            {emailError ? (
              <motion.p
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                transition={{ duration: prefersReducedMotion ? 0.12 : 0.18, ease: [0.22, 1, 0.36, 1] }}
                className="text-xs text-(--color-status-error)"
              >
                {emailError}
              </motion.p>
            ) : null}
          </AnimatePresence>

          <AnimatePresence initial={false}>
            {feedback ? (
              <motion.p
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                transition={{ duration: prefersReducedMotion ? 0.12 : 0.18, ease: [0.22, 1, 0.36, 1] }}
                className={`text-xs ${
                  feedbackTone === 'warning'
                    ? 'text-[color-mix(in_srgb,var(--color-status-warning)_88%,var(--color-text-secondary))]'
                    : 'text-(--color-status-error)'
                }`}
              >
                {feedback}
              </motion.p>
            ) : null}
          </AnimatePresence>

          <button
            type="submit"
            disabled={submitting}
            className="ca-btn-primary inline-flex h-11 w-full items-center justify-center px-4 text-sm font-semibold shadow-[0_10px_24px_-18px_color-mix(in_srgb,var(--color-action-primary)_85%,transparent)] transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <AnimatePresence mode="wait" initial={false}>
              {submitting ? (
                <motion.span
                  key="recovery-submitting"
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                  transition={{ duration: prefersReducedMotion ? 0.12 : 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex items-center gap-2"
                >
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <span>{submittingCta}</span>
                </motion.span>
              ) : (
                <motion.span
                  key="recovery-idle"
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                  transition={{ duration: prefersReducedMotion ? 0.12 : 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  {submitCta}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <p className="text-xs leading-relaxed text-(--color-text-secondary)">{submitHint}</p>
          <p className="text-xs leading-relaxed text-(--color-text-muted)">{securityNote}</p>
          <p className="text-xs leading-relaxed text-(--color-text-muted)">{helper}</p>
        </div>
      </form>

      <a
        href={loginHref}
        className="inline-flex w-full items-center justify-center text-sm font-semibold text-(--color-focus-ring) underline decoration-[color-mix(in_srgb,var(--color-focus-ring)_35%,transparent)] underline-offset-4 transition-colors duration-150 hover:text-(--color-text-primary)"
      >
        {backToLoginLabel}
      </a>
    </>
  )
}
